import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { compareStrings } from '../shared/utils';
import { dateToString } from '../shared/formatters';
import { loadingStart, loadingEnd } from './appMainSlice';
import { logoff } from './accountSlice';

var today = new Date();
const initialState = {
  items: [],
  result: {},
  shouldBeUpdated: true,
  lastUpdate: new Date().toISOString(),
  filterFields: {
    name: '',
    type: 'all',
    from: dateToString(new Date(today.getFullYear(), today.getMonth() - 6, 1)),
    to: dateToString(new Date(today.getFullYear(), today.getMonth() + 1, 0)),
  },
  order: { by: 'name', direction: 'asc' },
};

// Async thunks
export const loadCategory = createAsyncThunk(
  'category/loadCategory',
  async (_, { dispatch, getState, extra: api }) => {
    dispatch({ type: 'appMain/loadingStart' });
    try {
      const filters = getState().category.filterFields;
      const result = await api.loadCategory(filters);
      dispatch({ type: 'appMain/loadingEnd' });
      return { items: result.items };
    } catch (error) {
      dispatch({ type: 'appMain/loadingEnd' });
      if (error.isAxiosError) {
        dispatch({ type: 'account/logoff' });
        throw error;
      }
      // Convert error to serializable format
      const errorMessage = error.message || 'Um erro ocorreu, tente novamente';
      dispatch({ 
        type: 'appMain/showMessage', 
        payload: { 
          message: errorMessage,
          severity: 'error',
          type: 'error'
        } 
      });
      throw error;
    }
  }
);

export const addCategory = createAsyncThunk(
  'category/addCategory',
  async (item, { dispatch, getState, extra: api }) => {
    dispatch({ type: 'appMain/loadingStart' });
    try {
      await api.addCategory(item);
      dispatch({ type: 'appMain/loadingEnd' });
      dispatch({
        type: 'appMain/showMessage',
        payload: {
          message: 'Categoria criada com sucesso!',
          severity: 'success',
          type: 'success'
        },
      });
    } catch (error) {
      dispatch({ type: 'appMain/loadingEnd' });
      if (error.isAxiosError) {
        dispatch({ type: 'account/logoff' });
        throw error;
      }
      // Convert error to serializable format
      const errorMessage = error.message || 'Um erro ocorreu, tente novamente';
      dispatch({ 
        type: 'appMain/showMessage', 
        payload: { 
          message: errorMessage,
          severity: 'error',
          type: 'error'
        } 
      });
      throw error;
    }
  }
);

export const updateCategory = createAsyncThunk(
  'category/updateCategory',
  async (item, { dispatch, getState, extra: api }) => {
    dispatch({ type: 'appMain/loadingStart' });
    try {
      await api.updateCategory(item);
      dispatch({ type: 'appMain/loadingEnd' });
      dispatch({
        type: 'appMain/showMessage',
        payload: {
          message: 'Categoria atualizada com sucesso!',
          severity: 'success',
          type: 'success'
        },
      });
    } catch (error) {
      dispatch({ type: 'appMain/loadingEnd' });
      if (error.isAxiosError) {
        dispatch({ type: 'account/logoff' });
        throw error;
      }
      // Convert error to serializable format
      const errorMessage = error.message || 'Um erro ocorreu, tente novamente';
      dispatch({ 
        type: 'appMain/showMessage', 
        payload: { 
          message: errorMessage,
          severity: 'error',
          type: 'error'
        } 
      });
      throw error;
    }
  }
);

export const deleteCategory = createAsyncThunk(
  'category/deleteCategory',
  async (id, { dispatch, getState, extra: api }) => {
    dispatch({ type: 'appMain/loadingStart' });
    try {
      await api.deleteCategory(id);
      dispatch({ type: 'appMain/loadingEnd' });
      dispatch({
        type: 'appMain/showMessage',
        payload: {
          message: 'Categoria excluída com sucesso!',
          severity: 'success',
          type: 'success'
        },
      });
      return {};
    } catch (error) {
      dispatch({ type: 'appMain/loadingEnd' });
      if (error.isAxiosError) {
        dispatch({ type: 'account/logoff' });
        throw error;
      }
      // Convert error to serializable format
      const errorMessage = error.message || 'Um erro ocorreu, tente novamente';
      dispatch({ 
        type: 'appMain/showMessage', 
        payload: { 
          message: errorMessage,
          severity: 'error',
          type: 'error'
        } 
      });
      throw error;
    }
  }
);

export const loadAverageBalanceStart = createAsyncThunk(
  'category/loadAverageBalanceStart',
  async (filters, { dispatch, getState, extra: api }) => {
    dispatch(loadingStart());
    try {
      const result = await api.loadBalancePerCategory(filters);
      dispatch(loadingEnd());
      return result;
    } catch (error) {
      dispatch(loadingEnd());
      if (error.isAxiosError) {
        dispatch(logoff());
        throw error;
      }
      const errorMessage = error.message || 'Um erro ocorreu, tente novamente';
      dispatch({
        type: 'appMain/showMessage',
        payload: {
          message: errorMessage,
          severity: 'error',
          type: 'error'
        }
      });
      throw error;
    }
  }
);

const categorySlice = createSlice({
  name: 'category',
  initialState,
  reducers: {
    updateFilters: (state, action) => {
      state.shouldBeUpdated = true;
      state.filterFields = { ...action.payload };
    },
    updateCategoryOrder: (state, action) => {
      const sortedItems = [...state.items].sort((a, b) => {
        switch (action.payload.by) {
          case 'priority':
            return a.priority - b.priority;
          case 'type':
            return compareStrings(a.type, b.type);
          case 'group':
            var groupA = !!a.group ? a.group.name : '';
            var groupB = !!b.group ? b.group.name : '';
            return compareStrings(groupA, groupB);
          case 'name':
            return compareStrings(a.name, b.name);
          default:
            return 0;
        }
      });
      if (action.payload.direction === 'desc') {
        sortedItems.reverse();
      }
      state.items = sortedItems;
      state.order = action.payload;
    },
    clear: () => {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadCategory.fulfilled, (state, action) => {
        const sortedItems = [...action.payload.items].sort((a, b) => {
          switch (state.order.by) {
            case 'priority':
              return a.priority - b.priority;
            case 'type':
              return compareStrings(a.type, b.type);
            case 'group':
              var groupA = !!a.group ? a.group.name : '';
              var groupB = !!b.group ? b.group.name : '';
              return compareStrings(groupA, groupB);
            case 'name':
              return compareStrings(a.name, b.name);
            default:
              return 0;
          }
        });
        if (state.order.direction === 'desc') {
          sortedItems.reverse();
        }
        state.items = sortedItems;
        state.shouldBeUpdated = false;
        state.lastUpdate = new Date().toISOString();
      })
      .addCase(loadCategory.rejected, (state) => {
        state.shouldBeUpdated = true;
      })
      // Add category cases
      .addCase(addCategory.fulfilled, (state, action) => {
        state.shouldBeUpdated = true;
      })
      // Update category cases
      .addCase(updateCategory.fulfilled, (state, action) => {
        state.shouldBeUpdated = true;
      })
      // Delete category cases
      .addCase(deleteCategory.fulfilled, (state, action) => {
        state.shouldBeUpdated = true;
      })
      .addCase(loadAverageBalanceStart.fulfilled, (state, action) => {
        state.result = action.payload;
        state.shouldBeUpdated = false;
      });
  },
});

export const { updateFilters, toogleFilters, updateCategoryOrder, clear } =
  categorySlice.actions;

export default categorySlice.reducer;
